// filepath: /Users/austin/workspace/docuhounds/react-frontend/src/mobile/pages/MobileCourseDetailsPage.js
// NOTE: Added quick action buttons under details section
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import CourseDetailsSection from '../../components/details/CourseDetailsSection';

export default function MobileCourseDetailsPage(){
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { getEndeavor, getEndeavorEvents, getEndeavorAssignments } = useData();
  const [course,setCourse]=useState(null);
  const [events,setEvents]=useState([]);
  const [assignments,setAssignments]=useState([]);
  const [syllabusDocId] = useState(null);
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState(null);

  const formatDate = useCallback(d=>{
    if(!d) return 'Not set';
    const dt = d.includes('T')? new Date(d): new Date(d+'T00:00:00');
    if(isNaN(dt)) return 'Invalid date';
    return dt.toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'});
  },[]);

  const info = useMemo(()=>{
    if(!course) return {};
    return {
      typeName: course.endeavorTypeName || course.endeavorType?.name || 'Course',
      startDate: formatDate(course.startDate),
      endDate: formatDate(course.endDate),
      duration: `${formatDate(course.startDate)} - ${formatDate(course.endDate)}`,
      createdAt: formatDate(course.createdAt),
      updatedAt: formatDate(course.updatedAt)
    };
  },[course,formatDate]);

  useEffect(()=>{
    let active=true;
    (async()=>{
      try{
        setLoading(true);
        // ✅ Use DataContext which checks cache first (from /full endpoint)
        const cData = await getEndeavor(courseId);
        if(!active) return;
        setCourse(cData);

        // ✅ Extract events from course data if available (from /full endpoint), otherwise fetch from cache
        if (cData.events && Array.isArray(cData.events) && cData.events.length > 0) {
          setEvents(cData.events);
        } else {
          const eData = await getEndeavorEvents(courseId);
          if(!active) return;
          setEvents(Array.isArray(eData)?eData:[]);
        }
        
        // ✅ Fetch assignments using DataContext (checks cache from /full hydration)
        const aData = await getEndeavorAssignments(courseId);
        if(active) setAssignments(Array.isArray(aData)?aData:[]);
      }catch(e){
        if(active) setErr(e.message);
      }finally{
        if(active) setLoading(false);
      }
    })();
    return ()=>{active=false;};
  },[courseId, getEndeavor, getEndeavorEvents, getEndeavorAssignments]);

  if(loading) return <div className="m-screen"><p>Loading course...</p></div>;
  if(err) return <div className="m-screen"><p>Error: {err}</p></div>;
  if(!course) return <div className="m-screen"><p>Not found.</p></div>;

  return (
    <div className="m-screen">
      <CourseDetailsSection
        course={course}
        courseInfo={info}
        eventsCount={events.length}
        assignmentsCount={assignments.length}
        syllabusDocId={syllabusDocId}
        onEdit={()=>navigate(`/course/${courseId}/edit`)}
      />

      {/* Quick navigation buttons (4 buttons — wording in request said 3 but listed Documents too) */}
      <div className="m-course-actions-grid">
        <button
          type="button"
          className="m-course-action"
          onClick={()=>navigate(`/m/course/${courseId}/events`)}
        >
          📆 Course Events
          <span className="sub">{events.length}</span>
        </button>
        <button
          type="button"
          className="m-course-action"
          onClick={()=>navigate(`/m/course/${courseId}/assignments`)}
        >
          📝 Course Assignments
          <span className="sub">{assignments.length}</span>
        </button>
        <button
          type="button"
          className="m-course-action"
          onClick={()=>navigate(`/m/course/${courseId}/chat`)}
        >
          🤖 Chat With Course
        </button>
        <button
          type="button"
          className="m-course-action"
          onClick={()=>navigate(`/m/course/${courseId}/documents`)}
        >
          📄 Documents
        </button>
      </div>
    </div>
  );
}